import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useKeycloak } from "keycloak-react-web";
import { logout } from "../../app/common";
import { useTheme } from "../../app/ThemeContext";
import { AGENT_ADMIN_URL } from "../../app/utils";

import LabelIcon from "@mui/icons-material/Label";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import LinkIcon from "@mui/icons-material/Link";
import HistoryIcon from "@mui/icons-material/History";
import { ReactComponent as HomeIcon } from "../../assets/icons/home.svg";
import { ReactComponent as UserIcon } from "../../assets/icons/users.svg";
import { ReactComponent as VoucherIcon } from "../../assets/icons/vouchers.svg";
import ParkitIcon from "../../assets/icons/parkit_icon.png";
import DeliverItIcon from "../../assets/icons/deliverit_icon.png";
import { SUPER_ADMIN_AUTHORITY, hasKeycloakAuthority, hasStaffSignal } from "../../app/roles";
import { usePermissions } from "../../features/permissions/usePermissions";
import { parkitLandingPath } from "../ParkitNav/ParkitNav";
import { deliveritLandingPath } from "../DeliveritNav/DeliveritNav";
import { handleGetMyProfile, selectProfile } from "../../features/profile/profileSlice";
import NotificationBell from "./NotificationBell";
import FavouritesMenu from '../../features/favourites/FavouritesMenu';

export const pages = [
  // No permission. Home is where every other gate sends people, so it has to stay reachable.
  { label: "Home", path: "/home", icon: <HomeIcon className="w-4 h-4" /> },
  {
    label: "Vouchers",
    path: "/vouchers",
    icon: <VoucherIcon className="w-[18px] h-[18px]" />,
    requiredPermission: "voucher:read",
  },
  {
    label: "User Tags",
    path: "/user-tags",
    icon: <LabelIcon sx={{ fontSize: 16, color: "inherit" }} />,
    requiredPermission: "usertag:read",
  },
  // The two section tabs resolve their own destination rather than declaring a permission, because
  // each is the only door into a sidebar that only renders once you are already inside the section.
  // Gating them on their dashboard key would take a whole section away from somebody whose work is
  // in it but who does not happen to hold that one key.
  {
    label: "DeliverIt",
    path: "/deliverit",
    icon: <img alt="deliverit" src={DeliverItIcon} className="w-4 h-5" />,
    section: "deliverit",
  },
  {
    label: "ParkIt",
    path: "/parkit",
    icon: <img alt="parkit" src={ParkitIcon} className="w-5 h-5" />,
    section: "parkit",
  },
  {
    label: "Action Trail",
    path: "/action-trail",
    icon: <HistoryIcon sx={{ fontSize: 18, color: "inherit" }} />,
  },
  {
    label: "Staff",
    path: "/staff",
    icon: <UserIcon className="w-[18px] h-[18px]" />,
    // Hidden rather than shown-and-refused. Everyone in the company reaches this nav bar, and a tab
    // that always bounces you is worse than no tab: it invites a support ticket per employee.
    requiredAuthority: SUPER_ADMIN_AUTHORITY,
  },
  {
    label: "Chatbot",
    path: AGENT_ADMIN_URL,
    icon: <SmartToyIcon sx={{ fontSize: 16, color: "inherit" }} />,
    external: true,
    requiredPermission: "chatbot:manage",
  },
  {
    label: "Links",
    path: "/links",
    icon: <LinkIcon sx={{ fontSize: 16, color: "inherit" }} />,
    requiredAuthority: SUPER_ADMIN_AUTHORITY,
  },
];

/**
 * The tabs this person should see, with each section tab redirected to a screen they can reach.
 *
 * A plain function over the descriptor list so a test can assert the whole outcome — which tabs, and
 * where each points — without rendering a nav bar.
 */
export const visibleNavPages = (all, keycloak, can = () => true) =>
  all
    .map((page) => {
      if (page.section === "parkit") {
        return { ...page, path: parkitLandingPath(keycloak, can) };
      }
      if (page.section === "deliverit") {
        return { ...page, path: deliveritLandingPath(can) };
      }
      return page;
    })
    .filter((page) => {
      if (!page.path) return false;
      if (page.requiredAuthority && !hasKeycloakAuthority(keycloak, page.requiredAuthority)) return false;
      if (page.requiredPermission && !can(page.requiredPermission)) return false;
      if (page.requiredPermissions?.some((permission) => !can(permission))) return false;
      return true;
    });

/**
 * One nav tab. Internal destinations route client-side so the tab click does not reload the app;
 * the external entry (chatbot admin) stays a plain anchor because it leaves the portal entirely.
 */
const NavItem = ({ page, className, onNavigate, children }) =>
  page.external ? (
    <a href={page.path} className={className} onClick={onNavigate}>
      {children}
    </a>
  ) : (
    <Link to={page.path} className={className} onClick={onNavigate}>
      {children}
    </Link>
  );

const Nav = () => {
  const { keycloak } = useKeycloak();
  const dispatch = useDispatch();
  const profile = useSelector(selectProfile);
  const { can } = usePermissions();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setUserMenuOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [userMenuOpen]);

  const token = keycloak?.tokenParsed ?? {};
  const tokenName = token.name || [token.given_name, token.family_name].filter(Boolean).join(" ") || token.preferred_username || "Account";
  const profileName = profile.name || tokenName;
  const initial = profileName.trim().charAt(0).toUpperCase() || "A";

  const pathname = useLocation().pathname;
  const isActive = (page) => {
    if (page.external) return false;
    if (page.path === "/home") return pathname === "/" || pathname === "/home";
    return pathname.startsWith(page.path);
  };

  const visiblePages = hasStaffSignal(keycloak) ? visibleNavPages(pages, keycloak, can) : [];
  // Only chatbot ticket staff receive web notifications today.
  const showNotifications = !!keycloak.authenticated && hasStaffSignal(keycloak) && can("chatbot:manage");

  return (
    <nav id="appBar" className="sticky top-0 z-50 bg-nav border-b border-nav-border shadow-sm">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6">
        <div className="flex h-14 items-center justify-between">
          {/* Logo */}
          <Link to="/home" className="flex items-center gap-2.5 shrink-0 no-underline">
            <div className="w-8 h-8 rounded-lg bg-gradient-cta flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-sm tracking-tight">AP</span>
            </div>
            <span className="font-semibold text-theme hidden sm:block text-[15px]">
              Admin Portal
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-0.5">
            {visiblePages.map((page) => (
              <NavItem
                key={page.label}
                page={page}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium no-underline transition-colors ${
                  isActive(page)
                    ? "bg-primary/10 text-primary900"
                    : "text-theme-secondary hover:bg-surface-hover hover:text-theme"
                }`}
              >
                <span
                  className={`flex items-center ${
                    isActive(page) ? "text-primary" : "text-theme-muted"
                  }`}
                >
                  {page.icon}
                </span>
                {page.label}
                {page.external && (
                  <span className="text-theme-muted text-[11px] leading-none">&#8599;</span>
                )}
              </NavItem>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <FavouritesMenu />
            {/* Theme toggle */}
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 rounded-lg text-theme-secondary hover:bg-surface-hover transition-colors"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                </svg>
              )}
            </button>

            {showNotifications && <NotificationBell />}

            {/* Mobile hamburger */}
            <button
              type="button"
              className="md:hidden p-1.5 rounded-lg text-theme-secondary hover:bg-surface-hover transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobileNavMenu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>

            {/* User menu */}
            {!!keycloak.authenticated && (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center p-0.5 rounded-full hover:ring-2 hover:ring-surface-border transition-all focus:outline-none focus:ring-2 focus:ring-primary/40"
                  aria-label={`Open profile for ${profileName}`}
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-cta flex items-center justify-center">
                    <span className="text-white text-xs font-semibold">{initial}</span>
                  </div>
                </button>
                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div
                      role="menu"
                      aria-label="Profile"
                      className="absolute right-0 mt-2 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-2xl bg-surface-modal shadow-xl ring-1 ring-surface-border z-50"
                    >
                      <div className="px-5 py-4 border-b border-divider">
                        <p className="text-base font-semibold text-theme leading-6 break-words">{profileName}</p>
                        {profile.username && profile.username !== profileName && (
                          <p className="mt-0.5 text-xs text-theme-muted break-all">{profile.username}</p>
                        )}
                      </div>
                      <div className="px-5 py-4">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-theme-muted">Roles</p>
                        {profile.status === "loading" && profile.roles.length === 0 && (
                          <p className="mt-3 text-sm text-theme-secondary">Loading roles...</p>
                        )}
                        {profile.status === "failed" && profile.roles.length === 0 && (
                          <div className="mt-3 flex items-center justify-between gap-3">
                            <p className="text-sm text-theme-secondary">Roles unavailable</p>
                            <button
                              type="button"
                              onClick={() => dispatch(handleGetMyProfile())}
                              className="text-xs font-semibold text-primary hover:text-primary900"
                            >
                              Retry
                            </button>
                          </div>
                        )}
                        {profile.status === "succeeded" && profile.roles.length === 0 && (
                          <p className="mt-3 text-sm text-theme-secondary">No assigned roles</p>
                        )}
                        {profile.roles.length > 0 && (
                          <ul className="mt-2 max-h-56 overflow-y-auto divide-y divide-divider">
                            {profile.roles.map((role) => (
                              <li key={role.code} className="flex items-start justify-between gap-4 py-2.5 first:pt-1.5 last:pb-0">
                                <span className="min-w-0 text-sm font-medium text-theme-secondary leading-5 break-words">{role.name}</span>
                                <span className="shrink-0 rounded-md bg-surface-alt px-2 py-0.5 text-[11px] font-semibold tracking-wide text-theme-muted">
                                  {role.code}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="border-t border-divider p-2">
                      <button
                        onClick={logout}
                        role="menuitem"
                        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-theme-secondary hover:bg-surface-hover hover:text-theme transition-colors"
                      >
                        <svg className="w-4 h-4 text-theme-muted" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3-6l3 3m0 0l-3 3m3-3H9" />
                        </svg>
                        Logout
                      </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {!keycloak.authenticated && (
              <button
                onClick={() => keycloak.login()}
                className="px-4 py-1.5 text-sm font-medium text-white btn-cta rounded-lg transition-colors"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div id="mobileNavMenu" className="md:hidden border-t border-nav-border bg-nav">
          <div className="px-4 py-2 space-y-0.5">
            {visiblePages.map((page) => (
              <NavItem
                key={page.label}
                page={page}
                onNavigate={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium no-underline transition-colors ${
                  isActive(page)
                    ? "bg-primary/10 text-primary900"
                    : "text-theme-secondary hover:bg-surface-hover"
                }`}
              >
                <span
                  className={`flex items-center ${
                    isActive(page) ? "text-primary" : "text-theme-muted"
                  }`}
                >
                  {page.icon}
                </span>
                {page.label}
                {page.external && (
                  <span className="text-theme-muted text-[11px] leading-none">&#8599;</span>
                )}
              </NavItem>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Nav;
